import React, { useEffect, useRef } from 'react';
import {
  FaTimes,
  FaStar,
  FaShoppingCart,
  FaHeart,
  FaClock,
  FaLeaf,
} from 'react-icons/fa';
import { safeBase64Encode } from '../utils/safeEncode';
import './ExploreModal.css';

const ExploreModal = ({ isOpen, onClose, category, data }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleImageError = (e, itemName) => {
    const svgContent = `
      <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#FF7A3D"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
              fill="white" font-size="16" font-weight="bold" font-family="Sora, sans-serif">
          ${itemName}
        </text>
      </svg>
    `;
    
    const encodedSvg = safeBase64Encode(svgContent);
    if (encodedSvg) {
      e.target.src = `data:image/svg+xml;base64,${encodedSvg}`;
    } else {
      // Ultimate fallback - use URL encoding
      e.target.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
    }
  };

  if (!isOpen || !data) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div
        className="modal-container"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="category-badge">{category}</div>
            <h2 id="modal-title" className="modal-title">
              {data.title}
            </h2>
            <p className="modal-subtitle">{data.description}</p>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <div className="menu-grid">
            {data.items.map((item, index) => (
              <div key={index} className="menu-item">
                <div className="menu-item-image">
                  <img
                    src={item.image}
                    alt={item.name}
                    onError={(e) => handleImageError(e, item.name)}
                  />
                  <div className="item-overlay">
                    <button
                      className="favorite-btn"
                      aria-label="Add to favorites"
                    >
                      <FaHeart />
                    </button>
                  </div>
                  {item.badge && <div className="item-badge">{item.badge}</div>}
                </div>

                <div className="menu-item-content">
                  <h3 className="item-name">{item.name}</h3>
                  <p className="item-description">{item.description}</p>

                  <div className="item-details">
                    {item.rating && (
                      <div className="item-rating">
                        <FaStar />
                        <span>{item.rating}</span>
                      </div>
                    )}
                    {item.time && (
                      <div className="item-time">
                        <FaClock />
                        <span>{item.time}</span>
                      </div>
                    )}
                    {item.isVeg && (
                      <div className="item-veg">
                        <FaLeaf />
                        <span>Veg</span>
                      </div>
                    )}
                  </div>

                  <div className="item-footer">
                    <div className="item-price">
                      {item.originalPrice && (
                        <span className="original-price">
                          ₹{item.originalPrice}
                        </span>
                      )}
                      <span className="current-price">₹{item.price}</span>
                    </div>
                    <button className="add-to-cart-btn">
                      <FaShoppingCart />
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div className="modal-footer-content">
            <p>🚚 Free delivery on orders above ₹299</p>
            <button className="btn btn-primary view-cart-btn">
              <FaShoppingCart />
              View Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreModal;
